import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AuthShell } from "./AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formDef, setFormDef] = useState({ title: "Dołącz do Izby", description: "", fields: [] });
  const [values, setValues] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/reg-form").then((r) => setFormDef(r.data)).catch(() => {});
  }, []);

  const setVal = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const profile = {};
    (formDef.fields || []).forEach((f) => { profile[f.name] = values[f.name] || ""; });
    try {
      const u = await register({ name: values.name, email: values.email, password: values.password, profile });
      if (u && u.approved === false) {
        toast.success("Konto utworzone! Oczekuje na zatwierdzenie przez administratora.");
        navigate("/");
      } else {
        toast.success("Konto zostało utworzone. Witamy!");
        navigate("/forum");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={formDef.title} subtitle={formDef.description || "Utwórz konto członkowskie"}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label htmlFor="name">Imię i nazwisko</Label>
          <Input id="name" required value={values.name} onChange={(e) => setVal("name", e.target.value)} data-testid="register-name" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="email">E-mail</Label>
          <Input id="email" type="email" required value={values.email} onChange={(e) => setVal("email", e.target.value)} data-testid="register-email" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="password">Hasło</Label>
          <Input id="password" type="password" required minLength={8} value={values.password} onChange={(e) => setVal("password", e.target.value)} data-testid="register-password" className="mt-1.5" />
        </div>
        <p className="-mt-3 text-xs text-muted-foreground">Hasło musi mieć co najmniej 8 znaków.</p>
        {(formDef.fields || []).map((f) => (
          <div key={f.id || f.name}>
            <Label htmlFor={`f-${f.name}`}>{f.label}{f.required && " *"}</Label>
            <Input
              id={`f-${f.name}`}
              type={f.type === "number" ? "number" : "text"}
              required={f.required}
              value={values[f.name] || ""}
              onChange={(e) => setVal(f.name, e.target.value)}
              data-testid={`register-field-${f.name}`}
              className="mt-1.5"
            />
          </div>
        ))}
        {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full bg-ztk-blue hover:bg-ztk-navy800" data-testid="register-submit-button">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Utwórz konto
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Masz już konto?{" "}
        <Link to="/login" className="font-medium text-ztk-blue hover:underline" data-testid="register-login-link">Zaloguj się</Link>
      </p>
    </AuthShell>
  );
}
